/**
 * ConnectorStatus — the SINGLE status vocabulary for every connector.
 *
 * Ando historically told a connector's health through ~6 uncoordinated
 * components (HealthDot, StatusChip, StatusPill, DelegationChip, the
 * filesystem `deriveFsSyncView`, the WhatsApp Badge). This primitive
 * replaces them: one semantic state machine (`deriveConnectorStatus`) +
 * one presentational component with a few variants.
 *
 * States (most-actionable first — the precedence a user should act on):
 *   1. needs_reauth      → token expired / consent lapsed        (warning, pulse)
 *   2. syncing           → metadata.status pending/ingesting/…   (accent,  pulse)
 *   3. error             → unhealthy / degraded                  (error,   static)
 *   4. consent_expiring  → consent_expires_at within 14 days     (warning, static)
 *   5. connected         → healthy                               (success, static)
 *
 * Precedence 1-3 preserve the legacy `HealthDot` chain exactly, so migrating
 * the dot is byte-equivalent for OAuth/enterprise rows (which carry no
 * consent). Consent handling only affects PSD2 / Cassetto Fiscale rows and
 * derives from ``consent_expires_at`` — NEVER ``expires_at`` (the OAuth token
 * lifetime refreshes silently; using it for consent UI is a banned pattern).
 */

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { motion, useReducedMotion } from "framer-motion";
import type * as React from "react";

import type { ConnectionSummary } from "@ando/core";
import { cn } from "@ando/ui";

export type ConnectorStatusKind =
  | "connected"
  | "syncing"
  | "needs_reauth"
  | "consent_expiring"
  | "error";

/** Normalised inputs the state machine reads — decoupled from the wire shape. */
export interface ConnectorStatusSignals {
  readonly needsReauth: boolean;
  /** Lifecycle hint from ``metadata.status`` (pending/ingesting/processing/unhealthy). */
  readonly lifecycleStatus?: string | null;
  /** Enterprise connector reported ``degraded``/``unhealthy``. */
  readonly degraded?: boolean;
  /** REAL consent deadline (PSD2 / Cassetto). NOT the OAuth token expiry. */
  readonly consentExpiresAt?: string | null;
}

const PENDING_LIFECYCLE = new Set(["pending", "ingesting", "processing"]);
const CONSENT_WARN_MS = 14 * 24 * 60 * 60 * 1000;

/**
 * Pure state derivation. ``now`` is injectable so the consent thresholds are
 * deterministically testable.
 */
export function deriveConnectorStatus(
  s: ConnectorStatusSignals,
  now: number = Date.now(),
): ConnectorStatusKind {
  const consentAt = s.consentExpiresAt ? Date.parse(s.consentExpiresAt) : Number.NaN;
  const consentValid = Number.isFinite(consentAt);

  // 1 — needs action: token reauth, or a hard consent that has already lapsed.
  if (s.needsReauth) return "needs_reauth";
  if (consentValid && consentAt <= now) return "needs_reauth";

  // 2 — in-flight sync (matches the "Sincronizzazione in corso…" copy).
  if (s.lifecycleStatus && PENDING_LIFECYCLE.has(s.lifecycleStatus)) return "syncing";

  // 3 — unhealthy / degraded.
  if (s.lifecycleStatus === "unhealthy" || s.degraded === true) return "error";

  // 4 — consent approaching its deadline (soft warning).
  if (consentValid && consentAt - now <= CONSENT_WARN_MS) return "consent_expiring";

  // 5 — healthy.
  return "connected";
}

/** Extract the status signals from a wire ``ConnectionSummary``. */
export function signalsFromConnection(conn: ConnectionSummary): ConnectorStatusSignals {
  const meta = (conn.metadata ?? {}) as Record<string, unknown>;
  const lifecycle = typeof meta.status === "string" ? meta.status : null;
  return {
    needsReauth: conn.needs_reauth || conn.token_status === "needs_reauth",
    lifecycleStatus: lifecycle,
    degraded: meta.health === "degraded" || meta.health === "unhealthy",
    consentExpiresAt: conn.consent_expires_at ?? null,
  };
}

interface ToneSpec {
  readonly dot: string;
  readonly text: string;
  readonly pulse: boolean;
}

const TONES: Record<ConnectorStatusKind, ToneSpec> = {
  connected: { dot: "bg-success", text: "text-success", pulse: false },
  syncing: { dot: "bg-accent", text: "text-accent", pulse: true },
  needs_reauth: { dot: "bg-warning", text: "text-warning", pulse: true },
  consent_expiring: { dot: "bg-warning", text: "text-warning", pulse: false },
  error: { dot: "bg-error", text: "text-error", pulse: false },
};

/** Localised label for a status kind. Kept as a hook so it stays under Lingui. */
export function useConnectorStatusLabel(): (kind: ConnectorStatusKind) => string {
  const { i18n } = useLingui();
  return (kind) => {
    switch (kind) {
      case "connected":
        return i18n._(msg`Attivo`);
      case "syncing":
        return i18n._(msg`Sincronizzazione…`);
      case "needs_reauth":
        return i18n._(msg`Riconnessione richiesta`);
      case "consent_expiring":
        return i18n._(msg`Consenso in scadenza`);
      case "error":
        return i18n._(msg`Errore di connessione`);
    }
  };
}

const PulseDot: React.FC<{ tone: ToneSpec; size: string }> = ({ tone, size }) => {
  const reducedMotion = useReducedMotion();
  if (!tone.pulse || reducedMotion) {
    return (
      <span aria-hidden className={cn("inline-block shrink-0 rounded-full", size, tone.dot)} />
    );
  }
  return (
    <motion.span
      aria-hidden
      className={cn("inline-block shrink-0 rounded-full", size, tone.dot)}
      animate={{ opacity: [1, 0.4, 1] }}
      transition={{ duration: 1.4, repeat: Number.POSITIVE_INFINITY, ease: "easeInOut" }}
    />
  );
};

export interface ConnectorStatusProps {
  /** Pass a wire connection… */
  readonly connection?: ConnectionSummary;
  /** …or pre-derived signals (e.g. a mapped hub row). */
  readonly signals?: ConnectorStatusSignals;
  /**
   * ``dot`` — just the indicator (row leading glyph).
   * ``inline`` — dot + label (row trailing status, drawer header).
   * ``badge`` — a pill (header / catalog).
   */
  readonly variant?: "dot" | "inline" | "badge";
  readonly className?: string;
}

/**
 * The one status component. Give it a connection (or signals) and a variant.
 */
export const ConnectorStatus: React.FC<ConnectorStatusProps> = ({
  connection,
  signals,
  variant = "dot",
  className,
}) => {
  const label = useConnectorStatusLabel();
  const s: ConnectorStatusSignals =
    signals ?? (connection ? signalsFromConnection(connection) : { needsReauth: false });
  const kind = deriveConnectorStatus(s);
  const tone = TONES[kind];

  if (variant === "dot") {
    return <PulseDot tone={tone} size="h-1.5 w-1.5" />;
  }

  if (variant === "badge") {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 font-medium text-[10.5px]",
          tone.text,
          kind === "connected"
            ? "bg-success/15"
            : kind === "error"
              ? "bg-error/10"
              : kind === "syncing"
                ? "bg-accent/10"
                : "bg-warning/15",
          className,
        )}
      >
        <PulseDot tone={tone} size="h-1.5 w-1.5" />
        {label(kind)}
      </span>
    );
  }

  // inline
  return (
    <span className={cn("inline-flex items-center gap-1.5 text-[11px]", tone.text, className)}>
      <PulseDot tone={tone} size="h-1.5 w-1.5" />
      <span className="truncate">{label(kind)}</span>
    </span>
  );
};
ConnectorStatus.displayName = "ConnectorStatus";

/** Aggregate kind for a whole capability (header pill): worst-of the list. */
export function aggregateConnectorStatus(
  connections: readonly ConnectionSummary[],
  now: number = Date.now(),
): ConnectorStatusKind | "empty" {
  if (connections.length === 0) return "empty";
  const kinds = connections.map((c) => deriveConnectorStatus(signalsFromConnection(c), now));
  // Precedence for the aggregate: reauth > error > syncing > consent_expiring > connected.
  const order: ConnectorStatusKind[] = [
    "needs_reauth",
    "error",
    "syncing",
    "consent_expiring",
    "connected",
  ];
  for (const k of order) {
    if (kinds.includes(k)) return k;
  }
  return "connected";
}

/** Trans-wrapped labels re-exported for surfaces that want JSX (not the hook). */
export const ConnectorStatusText: React.FC<{ kind: ConnectorStatusKind }> = ({ kind }) => {
  switch (kind) {
    case "connected":
      return <Trans>Attivo</Trans>;
    case "syncing":
      return <Trans>Sincronizzazione…</Trans>;
    case "needs_reauth":
      return <Trans>Riconnessione richiesta</Trans>;
    case "consent_expiring":
      return <Trans>Consenso in scadenza</Trans>;
    case "error":
      return <Trans>Errore di connessione</Trans>;
  }
};
ConnectorStatusText.displayName = "ConnectorStatusText";
